/**
 * The data access the rules components are given. They never touch the app
 * context or axios themselves: a page builds an adapter (normally with
 * {@link restRulesApi}, since the three rules backends share one REST shape)
 * and hands it in, and a story hands in an in-memory one.
 */
import { useEffect, useState } from "react";
import type { AxiosInstance } from "axios";
import type { DefinitionRecord, RulePayload, RuleRecord, RuleVocabulary } from "./rules";

/** The rules of one definition. */
export interface RulesApi<TRule extends RuleRecord = RuleRecord> {
  list(includeDeleted?: boolean): Promise<TRule[]>;
  create(payload: RulePayload): Promise<TRule>;
  update(ruleId: string, payload: RulePayload): Promise<TRule>;
  remove(ruleId: string): Promise<unknown>;
}

/** The definitions of a feature, their rules and the editor vocabulary. */
export interface DefinitionsApi<
  TDefinition extends DefinitionRecord = DefinitionRecord,
  TRule extends RuleRecord = RuleRecord,
  TCreate = Partial<TDefinition>,
  TUpdate = Partial<TDefinition>
> {
  /** Identifies the backend for caches (the REST base path). */
  base: string;
  list(params?: { includeDeleted?: boolean; activeOnly?: boolean }): Promise<TDefinition[]>;
  get(id: string, includeDeletedRules?: boolean): Promise<TDefinition & { rules: TRule[] }>;
  create(payload: TCreate): Promise<TDefinition>;
  update(id: string, payload: TUpdate): Promise<TDefinition>;
  remove(id: string): Promise<{ success: boolean }>;
  vocabulary(): Promise<RuleVocabulary>;
  rules(definitionId: string): RulesApi<TRule>;
}

export interface RestRulesApiOptions {
  /**
   * Extra body fields a backend wants on rule creation beyond the shared
   * payload (the invoicing API's rule schema still carries `invoicingType`).
   */
  decorateRuleCreate?: (definitionId: string, payload: RulePayload) => Record<string, any>;
  /** Same for rule updates. */
  decorateRuleUpdate?: (ruleId: string, payload: RulePayload) => Record<string, any>;
  /** When the backend has no `/vocabulary`, what the editor offers instead. */
  staticVocabulary?: RuleVocabulary;
  /** What the editor offers when `/vocabulary` fails (a backend that does not serve it yet). */
  fallbackVocabulary?: RuleVocabulary;
}

/**
 * The REST shape the project-type, invoice-type and cost-code rule backends
 * share, rooted at `base`:
 *
 *   GET    base?includeDeleted&activeOnly   GET  base/vocabulary
 *   GET    base/{id}?includeDeletedRules    POST base   PATCH base/{id}   DELETE base/{id}
 *   GET    base/{id}/rules?includeDeleted   POST base/{id}/rules
 *   PATCH  base/rules/{ruleId}              DELETE base/rules/{ruleId}
 */
export const restRulesApi = <
  TDefinition extends DefinitionRecord = DefinitionRecord,
  TRule extends RuleRecord = RuleRecord,
  TCreate = Partial<TDefinition>,
  TUpdate = Partial<TDefinition>
>(
  client: AxiosInstance,
  base: string,
  options: RestRulesApiOptions = {}
): DefinitionsApi<TDefinition, TRule, TCreate, TUpdate> => ({
  base,
  list: async (params = {}) => {
    const { data } = await client.get<TDefinition[]>(base, { params });
    return data;
  },
  get: async (id, includeDeletedRules = false) => {
    const { data } = await client.get<TDefinition & { rules: TRule[] }>(`${base}/${id}`, {
      params: { includeDeletedRules },
    });
    return data;
  },
  create: async (payload) => {
    const { data } = await client.post<TDefinition>(base, payload);
    return data;
  },
  update: async (id, payload) => {
    const { data } = await client.patch<TDefinition>(`${base}/${id}`, payload);
    return data;
  },
  remove: async (id) => {
    const { data } = await client.delete<{ success: boolean }>(`${base}/${id}`);
    return data;
  },
  vocabulary: async () => {
    if (options.staticVocabulary) return options.staticVocabulary;
    try {
      const { data } = await client.get<RuleVocabulary>(`${base}/vocabulary`);
      return data;
    } catch (error) {
      if (options.fallbackVocabulary) return options.fallbackVocabulary;
      throw error;
    }
  },
  rules: (definitionId) => ({
    list: async (includeDeleted = false) => {
      const { data } = await client.get<TRule[]>(`${base}/${definitionId}/rules`, {
        params: { includeDeleted },
      });
      return data;
    },
    create: async (payload) => {
      const body = options.decorateRuleCreate
        ? { ...payload, ...options.decorateRuleCreate(definitionId, payload) }
        : payload;
      const { data } = await client.post<TRule>(`${base}/${definitionId}/rules`, body);
      return data;
    },
    update: async (ruleId, payload) => {
      const body = options.decorateRuleUpdate
        ? { ...payload, ...options.decorateRuleUpdate(ruleId, payload) }
        : payload;
      const { data } = await client.patch<TRule>(`${base}/rules/${ruleId}`, body);
      return data;
    },
    remove: async (ruleId) => {
      const { data } = await client.delete<{ success: boolean }>(`${base}/rules/${ruleId}`);
      return data;
    },
  }),
});

/* ------------------------------------------------------------------ */
/* Vocabulary hook (small, fetched once per backend per page load)      */
/* ------------------------------------------------------------------ */

const vocabularyCache = new Map<string, Promise<RuleVocabulary>>();

export const loadVocabulary = (
  api: Pick<DefinitionsApi, "base" | "vocabulary">,
  force = false
): Promise<RuleVocabulary> => {
  const cached = vocabularyCache.get(api.base);
  if (cached && !force) return cached;
  const pending = api.vocabulary().catch((error) => {
    vocabularyCache.delete(api.base);
    throw error;
  });
  vocabularyCache.set(api.base, pending);
  return pending;
};

/** Drop what is cached (tests, or after the backend's vocabulary changed). */
export const resetVocabularyCache = () => vocabularyCache.clear();

/**
 * The vocabulary of a rules backend, `null` until loaded. Cached by `api.base`
 * so every form on a page shares one request.
 */
export const useRuleVocabulary = (
  api: Pick<DefinitionsApi, "base" | "vocabulary">,
  onError?: (error: any) => void
): RuleVocabulary | null => {
  const [vocabulary, setVocabulary] = useState<RuleVocabulary | null>(null);
  useEffect(() => {
    let cancelled = false;
    loadVocabulary(api)
      .then((v) => {
        if (!cancelled) setVocabulary(v);
      })
      .catch((error) => onError?.(error));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api.base]);
  return vocabulary;
};
